/**
 * ClienteAuthContext
 * 
 * Provider de autenticação para clientes do cardápio digital.
 * Gerencia sessão via localStorage com tokens JWT.
 */
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { callEdgeFunction } from '@/lib/supabaseEdge';

interface ClienteAuth {
  id: number;
  nome: string;
  whatsapp: string;
  whatsapp_e164?: string;
}

interface ClienteAuthContextType {
  cliente: ClienteAuth | null;
  isLoggedIn: boolean;
  isLoading: boolean;
  login: (whatsapp: string, senha: string, lembrarSessao?: boolean, unidadeId?: number) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
  cadastrar: (dados: {
    whatsapp: string;
    nomeCompleto: string;
    senha: string;
    unidadeId?: number;
    lembrarSessao?: boolean;
  }) => Promise<{ success: boolean; error?: string }>;
  cadastrarViaAvaliacao: (dados: {
    tokenAvaliacao: string;
    nome: string;
    senha: string;
  }) => Promise<{ success: boolean; error?: string }>;
  enviarOtp: (whatsapp: string, tipo?: string) => Promise<{ success: boolean; error?: string; simulacao?: boolean; expiresIn?: number }>;
  verificarOtp: (whatsapp: string, codigo: string) => Promise<{ success: boolean; error?: string }>;
}

const ClienteAuthContext = createContext<ClienteAuthContextType | undefined>(undefined);

const STORAGE_KEY = 'sf_cliente_session';

// Decodificar token (suporta JWS sf_cliente_jwt_ e legado sf_cliente_)
function decodeToken(token: string): { sub: string; whatsapp: string; whatsapp_e164?: string; exp: number } | null {
  try {
    // Formato JWS assinado: sf_cliente_jwt_{header}.{payload}.{signature}
    if (token.startsWith('sf_cliente_jwt_')) {
      const compact = token.replace('sf_cliente_jwt_', '');
      const parts = compact.split('.');
      if (parts.length !== 3) return null;
      // Decodificar payload (parte do meio) — NÃO verifica assinatura (backend faz isso)
      const payloadB64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      const padded = payloadB64 + '='.repeat((4 - payloadB64.length % 4) % 4);
      return JSON.parse(atob(padded));
    }
    // Formato legado: sf_cliente_{base64(json)}
    if (token.startsWith('sf_cliente_')) {
      const payload = token.replace('sf_cliente_', '');
      return JSON.parse(atob(payload));
    }
    return null;
  } catch {
    return null;
  }
}

// Verificar se token está expirado
function isTokenExpired(token: string): boolean {
  const decoded = decodeToken(token);
  if (!decoded) return true;
  return decoded.exp < Math.floor(Date.now() / 1000);
}

export const ClienteAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [cliente, setCliente] = useState<ClienteAuth | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Normalização defensiva (evita falhas por espaços/cola acidental)
  const normalizeWhatsapp = useCallback((value: string) => value.replace(/\D/g, ''), []);
  const normalizeSenha = useCallback((value: string) => value.trim(), []);

  // Restaurar sessão ao carregar
  useEffect(() => {
    const restoreSession = async () => {
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (!stored) {
          setIsLoading(false);
          return;
        }

        const { token, cliente: storedCliente } = JSON.parse(stored);
        
        if (!token || isTokenExpired(token)) {
          localStorage.removeItem(STORAGE_KEY);
          setIsLoading(false);
          return;
        }

        // Validar campos obrigatórios do cliente
        // SEMPRE priorizar whatsapp do token JWT (normalizado) sobre o do localStorage
        const decoded = decodeToken(token);
        if (storedCliente?.id && storedCliente?.nome && decoded?.whatsapp) {
          const clienteRestaurado: ClienteAuth = {
            id: storedCliente.id,
            nome: storedCliente.nome,
            whatsapp: decoded.whatsapp, // Token JWT = fonte de verdade
            whatsapp_e164: decoded.whatsapp_e164,
          };
          // Atualizar localStorage com whatsapp do token
          localStorage.setItem(STORAGE_KEY, JSON.stringify({ token, cliente: clienteRestaurado }));
          setCliente(clienteRestaurado);
        } else if (storedCliente?.id && storedCliente?.nome && storedCliente?.whatsapp) {
          setCliente(storedCliente);
        } else {
          // Tentar recuperar dados do token JWT
          const decoded2 = decodeToken(token);
          if (decoded2 && storedCliente?.id && storedCliente?.nome) {
            // Restaurar whatsapp do token
            const clienteCompleto: ClienteAuth = {
              id: storedCliente.id,
              nome: storedCliente.nome,
              whatsapp: decoded2.whatsapp,
              whatsapp_e164: decoded2.whatsapp_e164,
            };
            // Atualizar localStorage com dados completos
            localStorage.setItem(STORAGE_KEY, JSON.stringify({ token, cliente: clienteCompleto }));
            setCliente(clienteCompleto);
            console.log('[ClienteAuth] Sessão restaurada com dados do token');
          } else {
            // Não conseguiu recuperar - forçar re-login
            console.warn('[ClienteAuth] Sessão inválida - campos obrigatórios faltando');
            localStorage.removeItem(STORAGE_KEY);
          }
        }
      } catch (error) {
        console.error('Erro ao restaurar sessão:', error);
        localStorage.removeItem(STORAGE_KEY);
      } finally {
        setIsLoading(false);
      }
    };

    restoreSession();
  }, []);

  // Salvar sessão
  const saveSession = useCallback((token: string, clienteData: ClienteAuth) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ token, cliente: clienteData }));
    setCliente(clienteData);
  }, []);

  // Login com suporte a auto-vínculo de unidade
  const login = useCallback(async (whatsapp: string, senha: string, lembrarSessao = true, unidadeId?: number) => {
    try {
      const whatsappNorm = normalizeWhatsapp(whatsapp);
      const senhaNorm = normalizeSenha(senha);

      if (!whatsappNorm || whatsappNorm.length < 10 || !senhaNorm) {
        return { success: false, error: 'WhatsApp ou senha inválidos' };
      }

      const { data, error } = await supabase.functions.invoke('cliente-login', {
        body: { whatsapp: whatsappNorm, senha: senhaNorm, lembrarSessao, unidadeId },
      });

      if (error) {
        console.error('Erro no login:', error);
        return { success: false, error: 'Erro ao fazer login' };
      }

      if (!data.success) {
        // Retorna código especial se cliente precisa se cadastrar
        return { 
          success: false, 
          error: data.error || 'Credenciais inválidas',
          codigo: data.codigo 
        };
      }

      saveSession(data.token, data.cliente);
      return { success: true };
    } catch (error) {
      console.error('Erro no login:', error);
      return { success: false, error: 'Erro de conexão' };
    }
  }, [normalizeSenha, normalizeWhatsapp, saveSession]);

  // Logout
  const logout = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setCliente(null);
  }, []);

  // Cadastrar
  const cadastrar = useCallback(async (dados: {
    whatsapp: string;
    nomeCompleto: string;
    senha: string;
    unidadeId?: number;
    lembrarSessao?: boolean;
  }) => {
    try {
      const { data, error } = await supabase.functions.invoke('cliente-cadastrar', {
        body: dados,
      });

      if (error) {
        console.error('Erro no cadastro:', error);
        return { success: false, error: 'Erro ao criar conta' };
      }

      if (!data.success) {
        return { success: false, error: data.error || 'Erro ao criar conta' };
      }

      saveSession(data.token, data.cliente);
      return { success: true };
    } catch (error) {
      console.error('Erro no cadastro:', error);
      return { success: false, error: 'Erro de conexão' };
    }
  }, [saveSession]);

  // Cadastrar via token de avaliação (sem OTP)
  const cadastrarViaAvaliacao = useCallback(async (dados: {
    tokenAvaliacao: string;
    nome: string;
    senha: string;
  }) => {
    try {
      const result = await callEdgeFunction<{ 
        success: boolean; 
        token: string; 
        cliente: { id: number; nome: string; whatsapp: string } 
      }>('cliente-cadastrar-via-avaliacao', dados);

      if (!result.success || !result.data?.success) {
        return { success: false, error: result.error || 'Erro ao criar conta' };
      }

      saveSession(result.data.token, result.data.cliente);
      return { success: true };
    } catch (error) {
      console.error('Erro no cadastro via avaliação:', error);
      return { success: false, error: 'Erro de conexão' };
    }
  }, [saveSession]);
  const enviarOtp = useCallback(async (whatsapp: string, tipo = 'cadastro') => {
    try {
      const result = await callEdgeFunction<{
        success: boolean;
        error?: string;
        simulacao?: boolean;
        expiresIn?: number;
      }>('enviar-otp-whatsapp', { whatsapp, tipo });

      if (!result.success) {
        return { success: false, error: result.error || 'Erro ao enviar código' };
      }

      return { 
        success: true, 
        simulacao: result.data?.simulacao, 
        expiresIn: result.data?.expiresIn || 300 
      };
    } catch (error) {
      console.error('Erro ao enviar OTP:', error);
      return { success: false, error: 'Erro de conexão' };
    }
  }, []);

  // Verificar OTP
  const verificarOtp = useCallback(async (whatsapp: string, codigo: string) => {
    try {
      const { data, error } = await supabase.functions.invoke('verificar-otp', {
        body: { whatsapp, codigo },
      });

      if (error) {
        console.error('Erro ao verificar OTP:', error);
        return { success: false, error: 'Erro ao verificar código' };
      }

      if (!data.success || !data.verified) {
        return { success: false, error: data.error || 'Código inválido ou expirado' };
      }

      return { success: true };
    } catch (error) {
      console.error('Erro ao verificar OTP:', error);
      return { success: false, error: 'Erro de conexão' };
    }
  }, []);

  const value = useMemo(() => ({
    cliente,
    isLoggedIn: !!cliente,
    isLoading,
    login,
    logout,
    cadastrar,
    cadastrarViaAvaliacao,
    enviarOtp,
    verificarOtp,
  }), [cliente, isLoading, login, logout, cadastrar, cadastrarViaAvaliacao, enviarOtp, verificarOtp]);

  return (
    <ClienteAuthContext.Provider value={value}>
      {children}
    </ClienteAuthContext.Provider>
  );
};

export const useClienteAuth = () => {
  const context = useContext(ClienteAuthContext);
  if (!context) {
    throw new Error('useClienteAuth deve ser usado dentro de ClienteAuthProvider');
  }
  return context;
};
